import { createTheme } from '@mui/material';

const typography = {
  fontFamily: 'Figtree',
  fontSize: 14,
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    '@media (min-width:0px)': {
      fontSize: '2rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '2.5rem', // sm breakpoint
    },
  },
  h2: {
    fontSize: '2.25rem',
    fontWeight: 700,
    '@media (min-width:0px)': {
      fontSize: '1.8rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '2.25rem', // sm breakpoint
    },
  },
  h3: {
    fontSize: '2rem',
    fontWeight: 700,
    '@media (min-width:0px)': {
      fontSize: '1.6rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '2rem', // sm breakpoint
    },
  },
  h4: {
    fontSize: '1.75rem',
    fontWeight: 700,
    '@media (min-width:0px)': {
      fontSize: '1.4rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '1.75rem', // sm breakpoint
    },
  },
  h5: {
    fontSize: '1.5rem',
    fontWeight: 700,
    '@media (min-width:0px)': {
      fontSize: '1.2rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '1.5rem', // sm breakpoint
    },
  },
  h6: {
    fontSize: '1.25rem',
    fontWeight: 700,
    '@media (min-width:0px)': {
      fontSize: '1rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '1.25rem', // sm breakpoint
    },
  },
  body1: {
    fontSize: '1rem',
    '@media (min-width:0px)': {
      fontSize: '0.9rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '1rem', // sm breakpoint
    },
  },
  body2: {
    fontSize: '0.8125rem',
    '@media (min-width:0px)': {
      fontSize: '0.8rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '0.8125rem', // sm breakpoint
    },
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 400,
    '@media (min-width:0px)': {
      fontSize: '0.9rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '1rem', // sm breakpoint
    },
  },
  subtitle2: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    '@media (min-width:0px)': {
      fontSize: '0.8rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '0.8125rem', // sm breakpoint
    },
  },
  caption: {
    fontSize: '0.6rem',
    fontWeight: 400,
    '@media (min-width:0px)': {
      fontSize: '0.57rem', // xs breakpoint
    },
    '@media (min-width:600px)': {
      fontSize: '0.6rem', // sm breakpoint
    },
  },
};

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#8175ff', // your current primary main for dark mode
      light: '#a1a1ff', // example light variant
      dark: '#5955cc', // example dark variant
      contrastText: '#fff',
    },
    secondary: {
      main: '#dc004e', // your current secondary main
      light: '#ff5c8d', // example light variant
      dark: '#9a0036', // example dark variant
      contrastText: '#fff',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#fff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#fff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#fff',
    },
    background: {
      default: '#fff',
      paper: '#fff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.85)',
      secondary: '#555',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  },
  typography: typography,
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'rgba(47, 43, 61, 0.14) 0px 2px 6px 0px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8175ff', // your current primary main for dark mode
      light: '#a1a1ff', // example light variant
      dark: '#5955cc', // example dark variant
      contrastText: '#fff',
    },
    secondary: {
      main: '#dc004e', // your current secondary main
      light: '#ff5c8d', // example light variant
      dark: '#9a0036', // example dark variant
      contrastText: '#fff',
    },
    error: {
      main: '#ff1e1e',
      light: '#ff6060',
      dark: '#e60000',
      contrastText: '#fff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#fff',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#fff',
    },
    success: {
      main: '#35d535',
      light: '#67e567',
      dark: '#2b9f2b',
      contrastText: '#fff',
    },
    background: {
      default: '#080808',
      paper: '#000000',
    },
    text: {
      primary: '#fff',
      secondary: '#aaa',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    action: {
      active: '#fff',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  typography: typography,
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'rgba(47, 43, 61, 0.14) 0px 2px 6px 0px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});
